import {
  ApostleBountyFilterInput,
  ApostleClassType,
  ApostleFilterInput,
  ApostleSkill,
  ApostleSkillType
} from '@apeiron/apostle'
import { NestedFilterItem } from '@apeiron/common'
import { noForwardCustomProps } from '@apeiron/temp'
import { styled } from '@mui/material'
import { ApostleFilterClassGroup } from '@src/components/apostles/list/filter/ApostleClassFilter'
import ApostleSkillItem from '@src/components/apostles/share/ApostleSkillItem'
import NestedFilter from '@src/components/share/filter/nestedFilter/NestedFilter'
import NestedFilterWrapper from '@src/components/share/filter/nestedFilter/NestedFilterWrapper'
import SkillDescriptionTab from '@src/components/share/skill/SkillDescriptionTab'
import { useApostleSkillsContext } from '@src/contexts/apostle/ApostleSkillsContext'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const SkillItem = styled(
  ApostleSkillItem,
  noForwardCustomProps(['popup'])
)<SkillFilterItemProps>`
  ${props => (props.popup ? '' : 'margin: 0px 8px;')}

  .Custom-Image-Container {
    .Custom-Image {
      width: 70px;
    }
    .Custom-Card-Type {
      display: none;
    }
  }

  .Custom-Mana {
    left: 9px;
    top: 11px;
  }

  .Custom-Info-Box {
    padding-top: 6px;
  }

  .Custom-Name-Box {
    .Custom-Name {
      color: #333333;
      font-weight: 400;
    }
    .Custom-Skill-Playback {
      .Custom-Icon {
        color: black;
        font-size: 24px;
      }
    }
  }

  .Custom-Description {
    color: #333333;
    font-weight: 400;
    margin-top: 6px;
  }
`

const FILTER_KEYS = [
  'firstSkillIDs',
  'otherSkillIDs'
] as const satisfies (keyof ApostleFilterInput)[]

type BasicSkillFilterInput = {
  class?: ApostleClassType[]
}

const ApostleSkillFilter = <
  T extends ApostleFilterInput | ApostleBountyFilterInput
>(
  props: Props<T>
) => {
  const { className, filterInput, onFilterChange } = props

  const { t } = useTranslation()

  const { filterSkills, searchSkills } = useApostleSkillsContext()

  const [selectedFirstSkills, setSelectedFirstSkills] = useState<
    NestedFilterItem[]
  >([])

  const [selectedIncludedSkills, setSelectedIncludedSkills] = useState<
    NestedFilterItem[]
  >([])

  const skills = useMemo(() => {
    return filterSkills({ types: [ApostleSkillType.ActiveSkill] })
  }, [filterSkills])

  const handleOnFilterInputChange = useCallback(
    (input: T) => {
      const typedInput = input as T

      onFilterChange(typedInput)
    },
    [onFilterChange]
  )

  const handleOnRenderItem = useCallback((item: NestedFilterItem) => {
    const skill = item as ApostleSkill

    return <SkillItem skill={skill} popup={false} />
  }, [])

  const handleOnSearchByText = useCallback((item: NestedFilterItem) => {
    const typedItem = item as ApostleSkill

    return typedItem.name || ''
  }, [])

  const handleOnSearchFirstItems = useCallback(
    (input: T) => {
      const result = searchSkills({
        keys: input.firstSkillIDs,
        types: [ApostleSkillType.ActiveSkill]
      })

      return result
    },
    [searchSkills]
  )

  const handleOnSearchOtherItems = useCallback(
    (input: T) => {
      const result = searchSkills({
        keys: input.otherSkillIDs,
        types: [ApostleSkillType.ActiveSkill]
      })

      return result
    },
    [searchSkills]
  )

  const handleOnSearchInnerItems = useCallback(
    (input: BasicSkillFilterInput) => {
      return filterSkills({
        classes: input.class,
        types: [ApostleSkillType.ActiveSkill]
      })
    },
    [filterSkills]
  )

  const handleOnRenderPopupFilter = useCallback(
    (
      input: BasicSkillFilterInput,
      onFilterItem: (filterInput: BasicSkillFilterInput) => void
    ) => {
      const handleSelectionsChange = (value: ApostleClassType[]) => {
        onFilterItem({
          ...input,
          class: value
        })
      }

      const selection = input.class || []

      return (
        <ApostleFilterClassGroup
          selectedValue={selection}
          onSelectionsChange={handleSelectionsChange}
          showTitle={false}
        />
      )
    },
    []
  )

  const handleOnReset = useCallback(() => {
    const newFilterInput = FILTER_KEYS.reduce((acc, field) => {
      delete acc[field]

      return acc
    }, filterInput)

    onFilterChange(newFilterInput)
    setSelectedFirstSkills([])
    setSelectedIncludedSkills([])
  }, [filterInput, onFilterChange])

  return (
    <NestedFilterWrapper
      className={className}
      title={t('apostle.filter.card_skills')}
      fields={FILTER_KEYS}
      filterInput={filterInput}
      handleOnReset={handleOnReset}
    >
      <NestedFilter
        additionalComponentInPopup={<SkillDescriptionTab />}
        allItems={skills}
        className={className}
        defaultSearchParam={{}}
        field={FILTER_KEYS[0]}
        title={t('apostle.filter.first_skill')}
        filterInput={filterInput}
        searchPlaceholder={t('filter.sections.skill.placeholder')}
        onFilterInputChange={handleOnFilterInputChange}
        renderPopupFilter={handleOnRenderPopupFilter}
        renderItem={handleOnRenderItem}
        searchByText={handleOnSearchByText}
        searchItems={handleOnSearchFirstItems}
        searchInnerItems={handleOnSearchInnerItems}
        selectedItems={selectedFirstSkills}
        setSelectedItems={setSelectedFirstSkills}
      />
      <NestedFilter
        additionalComponentInPopup={<SkillDescriptionTab />}
        allItems={skills}
        className={className}
        defaultSearchParam={{}}
        field={FILTER_KEYS[1]}
        title={t('apostle.filter.other_skills')}
        filterInput={filterInput}
        searchPlaceholder={t('filter.sections.skill.placeholder')}
        onFilterInputChange={handleOnFilterInputChange}
        renderPopupFilter={handleOnRenderPopupFilter}
        renderItem={handleOnRenderItem}
        searchByText={handleOnSearchByText}
        searchItems={handleOnSearchOtherItems}
        searchInnerItems={handleOnSearchInnerItems}
        selectedItems={selectedIncludedSkills}
        setSelectedItems={setSelectedIncludedSkills}
      />
    </NestedFilterWrapper>
  )
}

type SkillFilterItemProps = {
  popup: boolean
}

type Props<T extends ApostleFilterInput | ApostleBountyFilterInput> = {
  className?: string
  filterInput: T
  onFilterChange: (input: T) => void
}

export default ApostleSkillFilter
